import { Bar, Line } from "react-chartjs-2";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  type ChartOptions,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";

// Registered once at module load so the elements exist before any chart mounts
// (child chart effects run before a parent page effect would).
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

// Canvas needs concrete color values, so the categorical palette is defined here
// rather than via Tailwind/design-token classNames (the pattern every chart in
// this repo follows). Ordered for good adjacent contrast.
const PALETTE = [
  "#4f46e5",
  "#0ea5e9",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#a855f7",
  "#ec4899",
  "#14b8a6",
  "#64748b",
  "#84cc16",
];

const BAR_OPTIONS: ChartOptions<"bar"> = {
  responsive: true,
  maintainAspectRatio: false,
  animation: false,
  plugins: {
    legend: { display: false },
  },
  scales: {
    y: { beginAtZero: true, ticks: { precision: 0 } },
  },
};

const LINE_OPTIONS: ChartOptions<"line"> = {
  responsive: true,
  maintainAspectRatio: false,
  animation: false,
  plugins: {
    legend: { display: false },
  },
  scales: {
    y: { beginAtZero: true, ticks: { precision: 0 } },
  },
};

interface LabeledCount {
  label: string;
  count: number;
}

export const DistributionBarChart = ({ data }: { data: LabeledCount[] }) => (
  <div className="h-64">
    <Bar
      data={{
        labels: data.map((d) => d.label),
        datasets: [
          {
            label: "Queries",
            data: data.map((d) => d.count),
            backgroundColor: data.map((_, i) => PALETTE[i % PALETTE.length]),
            borderRadius: 4,
          },
        ],
      }}
      options={BAR_OPTIONS}
    />
  </div>
);

interface DayCount {
  day: string;
  count: number;
}

const formatDay = (day: string): string => {
  const parsed = new Date(day);
  return Number.isNaN(parsed.getTime())
    ? day
    : parsed.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

export const VolumeLineChart = ({ data }: { data: DayCount[] }) => (
  <div className="h-64">
    <Line
      data={{
        labels: data.map((p) => formatDay(p.day)),
        datasets: [
          {
            label: "Classified messages",
            data: data.map((p) => p.count),
            borderColor: PALETTE[0],
            backgroundColor: PALETTE[0],
            tension: 0.3,
            pointRadius: 2,
            fill: false,
          },
        ],
      }}
      options={LINE_OPTIONS}
    />
  </div>
);
